// Migrated from src/pages/staff/ZoneDashboard.js (322 LOC) in PR 12 ST 7.
// Mirrors PR 11 ST 10's StaffDashboard treatment for exhibits:
//   • axios → typed wrappers via useZoneList / useDeleteZone /
//     useGenerateZoneQR / useTransitionZone.
//   • Publish toggle reads `zone.state` and dispatches transitionZone.
//   • Image src reads `cover_image_id` (PR 14 ST 4); the legacy
//     `image_url` derived field was retired.
//   • ApiError messages bubble through naturally (the client
//     interceptor synthesises an ApiError; the catch surfaces
//     err.message); details.codes preserved when present.

import { useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, Pencil, Trash2, QrCode, MapPin } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";
import { ApiError } from "@shared/api/client";
import type { ZoneListItem } from "@shared/api/zones";
import {
  useDeleteZone,
  useGenerateZoneQR,
  useTransitionZone,
  useZoneList,
} from "@shared/api/hooks/useZones";
import { useExhibitions } from "@shared/api/hooks/useExhibitions";
import type { ExhibitionListItem } from "@shared/api/exhibitions";
import type { ListStatus } from "@shared/api/listStatus";
import { assetUrl } from "@shared/api/assets";
import StatusFilter from "@staff/components/StatusFilter";

function imageSrc(zone: ZoneListItem): string | null {
  return zone.coverImageId ? assetUrl(zone.coverImageId) : null;
}

function publishErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const codes = Array.isArray(err.details)
      ? err.details
          .map((d) => (d as { code?: string } | undefined)?.code)
          .filter(Boolean)
          .join(", ")
      : "";
    return codes ? `${err.message} (${codes})` : err.message;
  }
  return "Failed to update publish status";
}

const ZoneDashboard: FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState<ListStatus>("active");

  const { data: zones = [], isLoading } = useZoneList(status);
  // F-B follow-up: parent-exhibition lookup so each zone row shows its
  // owning exhibition. Both queries are cached; the join is client-side
  // and free at HKDC scale.
  // F-C: include archived + purged parents in the lookup map so an
  // admin viewing a cascade-archived zone still sees its lineage
  // (otherwise the column would render an em-dash for orphaned zones
  // and the relationship is inscrutable). Visual treatment surfaces
  // the parent's state in the row.
  const { data: exhibitions = [] } = useExhibitions({ includeArchived: true });
  const exMap = useMemo(
    () =>
      Object.fromEntries(
        exhibitions.map((e: ExhibitionListItem) => [e.id, e]),
      ) as Record<string, ExhibitionListItem>,
    [exhibitions],
  );
  const deleteZone = useDeleteZone();
  const generateQR = useGenerateZoneQR();
  const transitionZone = useTransitionZone();

  const filteredZones = useMemo(
    () =>
      zones.filter(
        (zone) =>
          zone.titleTc.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (zone.titleEn || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
      ),
    [zones, searchQuery],
  );

  const handleTogglePublish = async (zone: ZoneListItem) => {
    const nextState = zone.state === "published" ? "draft" : "published";
    try {
      await transitionZone.mutateAsync({
        zoneId: zone.id,
        payload: { to_state: nextState },
      });
      toast.success(
        nextState === "published" ? "Zone published" : "Zone unpublished",
      );
    } catch (err) {
      toast.error(publishErrorMessage(err));
    }
  };

  const handleDelete = async (zoneId: string) => {
    try {
      await deleteZone.mutateAsync(zoneId);
      toast.success("Zone deleted");
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Failed to delete zone";
      toast.error(message);
    }
  };

  const handleGenerateQR = async (zoneId: string) => {
    try {
      await generateQR.mutateAsync(zoneId);
      toast.success("QR code generated");
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Failed to generate QR code";
      toast.error(message);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="zone-dashboard-page"
    >
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/staff/exhibitions")}
              variant="ghost"
              size="sm"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold">
                Zone Management
              </h1>
              <p className="text-sm text-muted-foreground">
                Organize exhibits into zones
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder="Search zones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
            data-testid="search-input"
          />
          <StatusFilter value={status} onChange={setStatus} />
          <Button
            onClick={() => navigate("/staff/zone/create")}
            data-testid="create-zone-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Zone
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-12" data-testid="loading-state">
            Loading...
          </div>
        ) : filteredZones.length === 0 ? (
          <div
            className="text-center py-12 border border-border rounded-lg"
            data-testid="empty-state"
          >
            <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No zones found</p>
            <Button onClick={() => navigate("/staff/zone/create")}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Zone
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="zones-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Code
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Image
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Title (TC)
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Exhibition
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Exhibits
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Status
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      QR Code
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredZones.map((zone) => {
                    const img = imageSrc(zone);
                    const published = zone.state === "published";
                    const isInactive =
                      zone.state === "archived" || zone.state === "purged";
                    return (
                      <tr key={zone.id} data-testid={`zone-row-${zone.id}`}>
                        <td className="px-6 py-4">
                          <div className="font-mono text-2xl font-bold text-primary">
                            {zone.code || "---"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {img ? (
                            <img
                              src={img}
                              alt={zone.titleTc}
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                              <MapPin className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium">{zone.titleTc}</div>
                          <div className="text-sm text-muted-foreground">
                            {zone.titleEn}
                          </div>
                        </td>
                        <td
                          className="px-6 py-4"
                          data-testid={`zone-exhibition-${zone.id}`}
                        >
                          {zone.exhibitionId && exMap[zone.exhibitionId] ? (
                            <div>
                              <div className="font-medium">
                                {exMap[zone.exhibitionId].titleTc}
                                {exMap[zone.exhibitionId].state ===
                                  "archived" && (
                                  <span className="ml-2 text-xs text-amber-700">
                                    ▼ archived
                                  </span>
                                )}
                                {exMap[zone.exhibitionId].state ===
                                  "purged" && (
                                  <span className="ml-2 text-xs text-zinc-500">
                                    ✕ purged
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {exMap[zone.exhibitionId].code}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              {zone.exhibitCount || 0} exhibits
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {zone.state === "archived" ? (
                            <span
                              className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                              title="Restore the parent exhibition to bring this zone back"
                              data-testid={`publish-toggle-${zone.id}`}
                            >
                              ▼ Archived
                            </span>
                          ) : zone.state === "purged" ? (
                            <span
                              className="px-3 py-1 rounded-full text-xs font-medium bg-zinc-200 text-zinc-600"
                              title="Zone has been purged — tombstone only, asset bytes removed"
                              data-testid={`publish-toggle-${zone.id}`}
                            >
                              ✕ Purged
                            </span>
                          ) : (
                            <button
                              onClick={() => handleTogglePublish(zone)}
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                published
                                  ? "bg-green-100 text-green-700"
                                  : "bg-zinc-100 text-zinc-700"
                              }`}
                              data-testid={`publish-toggle-${zone.id}`}
                            >
                              {published ? "Published" : "Unpublished"}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {zone.qrCodeUrl ? (
                            <span
                              className="text-green-600 text-sm"
                              data-testid={`qr-status-${zone.id}`}
                            >
                              Generated
                            </span>
                          ) : isInactive ? (
                            <span className="text-muted-foreground text-sm">
                              —
                            </span>
                          ) : (
                            <Button
                              onClick={() => handleGenerateQR(zone.id)}
                              variant="outline"
                              size="sm"
                              data-testid={`generate-qr-${zone.id}`}
                            >
                              <QrCode className="w-4 h-4 mr-1" />
                              Generate
                            </Button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() =>
                                navigate(`/staff/zone/${zone.id}/edit`)
                              }
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-button-${zone.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {!isInactive && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive"
                                    data-testid={`delete-button-${zone.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Delete Zone
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure? This will remove the zone
                                      but not the linked exhibits.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(zone.id)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneDashboard;
